import React, { useEffect, useState, useCallback } from 'react'
import { Helmet } from 'react-helmet-async'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import {
    fetchGigs,
    fetchCategories,
    fetchFeaturedGigs,
    searchGigs,
    setFilters,
    clearFilters,
    setCurrentPage
} from '@/store/slices/gigsSlice'
import { gigApi } from '@/services/api/gigs'
import { GigFilters as GigFiltersType, Gig } from '@/types/gig'
import GigCard from '@/components/gigs/GigCard'
import GigFilters from '@/components/gigs/GigFilters'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import Modal from '@/components/ui/Modal'
import {
    AdjustmentsHorizontalIcon,
    SparklesIcon,
    EyeSlashIcon,
    ExclamationTriangleIcon
} from '@heroicons/react/24/outline'

import { useSnackbarContext } from '@/components/ui/SnackbarProvider'

const GigsPage: React.FC = () => {
    const { showSuccess, showInfo, showError } = useSnackbarContext()
    const dispatch = useAppDispatch()
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()

    const {
        gigs,
        featuredGigs,
        totalCount,
        currentPage,
        totalPages,
        filters,
        isLoading,
        error,
        categories
    } = useAppSelector(state => state.gigs)

    const currentUser = useAppSelector(state => state.auth.user)
    const [favoriteGigs, setFavoriteGigs] = useState<Set<string>>(new Set())

    // Fetch user's favorite gigs on mount
    useEffect(() => {
        let isMounted = true
        
        const fetchUserFavorites = async () => {
            if (!currentUser) return

            try {
                const response = await gigApi.getFavoriteGigs({ page: 1, limit: 1000 })
                
                // Only update state if component is still mounted
                if (!isMounted) return
                
                if (response.success) {
                    const gigs: Gig[] = response.data?.gigs ?? []
                    const favoriteIds = new Set<string>(gigs.map((gig: Gig) => gig._id as string))
                    setFavoriteGigs(favoriteIds)
                } else {
                    setFavoriteGigs(new Set())
                }
            } catch (error: any) {
                // Only log errors if component is still mounted
                // Ignore abort errors as they're expected when navigating away
                if (isMounted && error?.code !== 'ECONNABORTED') {
                    console.error('Failed to fetch favorite gigs:', error)
                }
            }
        }

        fetchUserFavorites()
        
        // Cleanup function
        return () => {
            isMounted = false
        }
    }, [currentUser])

    const [showFilters, setShowFilters] = useState(false)
    const [showMobileFiltersModal, setShowMobileFiltersModal] = useState(false)
    const [refreshing, setRefreshing] = useState(false)
    const [urlParamsProcessed, setUrlParamsProcessed] = useState(false)

    // Process URL parameters on mount and when location changes
    useEffect(() => {
        const urlFilters: Partial<GigFiltersType> = {}

        // Check for featured parameter
        const featuredParam = searchParams.get('featured')
        if (featuredParam === 'true') {
            urlFilters.featured = true
        }

        // Check for category parameter
        const categoryParam = searchParams.get('category')
        if (categoryParam) {
            urlFilters.category = categoryParam
        }

        // Check for location parameter
        const locationParam = searchParams.get('location')
        if (locationParam) {
            urlFilters.location = locationParam
        }

        // Check for other parameters
        const minPayParam = searchParams.get('minPay')
        if (minPayParam) {
            urlFilters.minPay = Number(minPayParam)
        }

        const maxPayParam = searchParams.get('maxPay')
        if (maxPayParam) {
            urlFilters.maxPay = Number(maxPayParam)
        }

        const paymentTypeParam = searchParams.get('paymentType')
        if (paymentTypeParam) {
            urlFilters.paymentType = paymentTypeParam as 'hourly' | 'fixed' | 'commission'
        }

        const urgencyParam = searchParams.get('urgency')
        if (urgencyParam) {
            urlFilters.urgency = urgencyParam as 'low' | 'medium' | 'high' | 'urgent'
        }

        // Check for search parameter
        const searchParam = searchParams.get('search')
        if (searchParam) {
            urlFilters.query = searchParam
        }

        // Only update filters if there are URL parameters and we haven't processed them yet
        if (Object.keys(urlFilters).length > 0 && !urlParamsProcessed) {
            dispatch(setFilters(urlFilters))
            setUrlParamsProcessed(true)
        } else if (Object.keys(urlFilters).length === 0 && urlParamsProcessed) {
            // Reset if URL parameters are cleared but maintain URL processing state
            setUrlParamsProcessed(false)
        }
    }, [searchParams, dispatch, urlParamsProcessed])

    // Fetch initial data
    useEffect(() => {
        dispatch(fetchCategories())
        dispatch(fetchFeaturedGigs())
    }, [dispatch])

    // Fetch gigs when filters change (including from URL parameters)
    useEffect(() => {
        if (filters.query && filters.query.trim()) {
            // Use search API when there's a search query
            dispatch(searchGigs({ query: filters.query, filters }))
        } else {
            // Use regular fetch when no search query
            dispatch(fetchGigs({ page: 1, limit: 12, filters }))
        }
        dispatch(setCurrentPage(1))
    }, [dispatch, filters])

    // Update filters (fetchGigs will be triggered by useEffect)
    const handleFiltersChange = useCallback((newFilters: Partial<GigFiltersType>) => {
        dispatch(setFilters(newFilters))
    }, [dispatch])

    // Handle page change
    const handlePageChange = (page: number) => {
        dispatch(setCurrentPage(page))
        dispatch(fetchGigs({ page, limit: 12, filters }))
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    // Handle refresh
    const handleRefresh = async () => {
        setRefreshing(true)
        try {
            await dispatch(fetchGigs({ page: currentPage, limit: 12, filters })).unwrap()
            await dispatch(fetchFeaturedGigs()).unwrap()
        } catch (error) {
            console.error('Failed to refresh gigs:', error)
        } finally {
            setRefreshing(false)
        }
    }

    // Handle favorite toggle using gig favorites API
    const handleFavoriteToggle = async (gigId: string) => {
        const wasInFavorites = favoriteGigs.has(gigId)

        // Optimistically update UI
        const newFavorites = new Set(favoriteGigs)
        if (wasInFavorites) {
            newFavorites.delete(gigId)
        } else {
            newFavorites.add(gigId)
        }
        setFavoriteGigs(newFavorites)

        try {
            const response = await gigApi.toggleFavorite(gigId)
            if (response.success) {
                if (!wasInFavorites) {
                    showSuccess('Added to favorites')
                } else {
                    showInfo('Removed from favorites')
                }
            }
        } catch (error: any) {
            // Revert optimistic update on error
            setFavoriteGigs(favoriteGigs)
            const message = error.response?.data?.error?.message || 'Failed to toggle favorite'
            showError(message)
        }
    }

    // Clear all filters (fetchGigs will be triggered by useEffect)
    const handleClearFilters = () => {
        dispatch(clearFilters())
        setUrlParamsProcessed(false)
        // Navigate back to base URL without query parameters
        navigate('/gigs', { replace: true })
    }

    // Check if any filters are active
    const hasActiveFilters = !!(
        filters.category ||
        filters.subcategory ||
        filters.location ||
        filters.minPay ||
        filters.maxPay ||
        filters.paymentType ||
        filters.urgency ||
        filters.featured
    )

    const visibleFrom = totalCount > 0 ? ((currentPage - 1) * 12) + 1 : 0
    const visibleTo = totalCount > 0 ? Math.min(currentPage * 12, totalCount) : 0

    // Handle filter button click - show modal on mobile, toggle sidebar on desktop
    const handleFiltersToggle = () => {
        // Check if we're on mobile (screen width < 768px)
        const isMobile = window.innerWidth < 768
        if (isMobile) {
            setShowMobileFiltersModal(!showMobileFiltersModal)
        } else {
            setShowFilters(!showFilters)
        }
    }

    return (
        <>
            <Helmet>
                <title>{filters.featured ? 'Featured Gigs' : 'Browse Gigs'} - SideGO</title>
                <meta name="description" content="Find the perfect gig opportunities worldwide. Browse by category, location, and more." />
            </Helmet>

            <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
                <div className="mb-6 overflow-hidden rounded-3xl border border-slate-200 bg-white/90 p-5 shadow-sm backdrop-blur dark:border-gray-800 dark:bg-gray-900/80 sm:mb-8 sm:p-6">
                    <div className="mb-4 flex flex-wrap items-center gap-2">
                        <span className="inline-flex items-center gap-1 rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-cyan-700 dark:border-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-300">
                            <SparklesIcon className="h-3.5 w-3.5" />
                            Gig Discovery
                        </span>
                        {hasActiveFilters && (
                            <span className="inline-flex items-center rounded-full border border-primary-200 bg-primary-50 px-3 py-1 text-xs font-semibold text-primary-700 dark:border-primary-800 dark:bg-primary-900/30 dark:text-primary-300">
                                Filtered Results
                            </span>
                        )}
                    </div>

                    <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                        <div>
                            <h1 className="mb-2 text-3xl font-bold text-slate-900 dark:text-white sm:text-4xl">
                                {filters.featured ? 'Featured Gigs' : 'Browse Gigs'}
                            </h1>
                            <p className="text-sm text-slate-600 dark:text-gray-400 sm:text-base">
                                {totalCount > 0
                                    ? `Showing ${visibleFrom}-${visibleTo} of ${totalCount} gigs`
                                    : 'Explore opportunities posted by trusted employers worldwide'}
                            </p>
                        </div>

                        <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center lg:w-auto">
                            <button
                                onClick={handleFiltersToggle}
                                className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition-all duration-200 hover:-translate-y-0.5 hover:bg-slate-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                            >
                                <AdjustmentsHorizontalIcon className="h-5 w-5" />
                                <span>Filters</span>
                                {hasActiveFilters && (
                                    <span className="rounded-full bg-primary-100 px-2 py-0.5 text-xs font-semibold text-primary-700 dark:bg-primary-900/50 dark:text-primary-300">
                                        Active
                                    </span>
                                )}
                            </button>

                            <button
                                onClick={handleRefresh}
                                disabled={refreshing}
                                className="inline-flex items-center justify-center rounded-xl bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:-translate-y-0.5 hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                {refreshing ? 'Refreshing...' : 'Refresh'}
                            </button>
                        </div>
                    </div>
                </div>

                <div className="flex gap-4 lg:gap-6">
                    {/* Filters Sidebar - Hidden on mobile, positioned at top */}
                    {showFilters && (
                        <div className="hidden md:block w-80 lg:w-84 flex-shrink-0">
                            <div className="sticky top-6">
                                <GigFilters
                                    filters={filters}
                                    onFiltersChange={handleFiltersChange}
                                    categories={categories}
                                />
                            </div>
                        </div>
                    )}

                    <div className="flex-1 min-w-0">
                        {/* Featured Gigs Section */}
                        {featuredGigs && featuredGigs.length > 0 && !hasActiveFilters && (
                            <div className="mb-8 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900 sm:p-5">
                                <h2 className="mb-4 text-xl font-semibold text-slate-900 dark:text-white">
                                    Featured Picks
                                </h2>
                                <div className={`grid grid-cols-1 gap-6 mb-6 ${showFilters
                                    ? 'md:grid-cols-2' // Only 2 columns when sidebar is open
                                    : 'md:grid-cols-2 lg:grid-cols-3' // 3 columns when sidebar is closed
                                    }`}>
                                    {featuredGigs.slice(0, 3).map((gig) => {
                                        return (
                                            <GigCard
                                                key={gig._id}
                                                gig={gig}
                                                onFavorite={handleFavoriteToggle}
                                                isFavorited={favoriteGigs.has(gig._id)}
                                                currentUser={currentUser}
                                            />
                                        )
                                    })}
                                </div>
                                <div className="mt-2 border-t border-slate-200 dark:border-gray-700"></div>
                            </div>
                        )}

                        {/* Active Filters Bar */}
                        {hasActiveFilters && (
                            <div className="mb-6 rounded-2xl border border-primary-200 bg-primary-50/80 p-4 dark:border-primary-800 dark:bg-primary-900/20">
                                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                    <div className="flex flex-wrap items-center gap-2 text-sm text-primary-700 dark:text-primary-300">
                                        <AdjustmentsHorizontalIcon className="w-4 h-4" />
                                        <span>Filters applied</span>
                                        {filters.featured && (
                                            <span className="rounded-md bg-primary-100 px-2 py-1 dark:bg-primary-800">
                                                Featured
                                            </span>
                                        )}
                                        {filters.category && (
                                            <span className="rounded-md bg-primary-100 px-2 py-1 dark:bg-primary-800">
                                                {filters.category}
                                            </span>
                                        )}
                                        {filters.location && (
                                            <span className="rounded-md bg-primary-100 px-2 py-1 dark:bg-primary-800">
                                                {filters.location}
                                            </span>
                                        )}
                                    </div>
                                    <button
                                        onClick={handleClearFilters}
                                        className="inline-flex items-center justify-center rounded-lg border border-primary-200 bg-white px-3 py-1.5 text-sm font-medium text-primary-700 transition-colors hover:bg-primary-100 dark:border-primary-700 dark:bg-primary-900/10 dark:text-primary-300 dark:hover:bg-primary-900/40"
                                    >
                                        Clear all
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Loading State */}
                        {isLoading && !refreshing && (
                            <div className="flex items-center justify-center rounded-2xl border border-slate-200 bg-white py-16 dark:border-gray-800 dark:bg-gray-900">
                                <LoadingSpinner size="xl" />
                            </div>
                        )}

                        {/* Error State */}
                        {error && (
                            <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-center dark:border-red-800 dark:bg-red-900/20">
                                <ExclamationTriangleIcon className="w-12 h-12 text-red-500 mx-auto mb-4" />
                                <h3 className="text-lg font-medium text-red-700 dark:text-red-400 mb-2">
                                    Failed to load gigs
                                </h3>
                                <p className="text-red-600 dark:text-red-300 mb-4">{error}</p>
                                <button
                                    onClick={handleRefresh}
                                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                                >
                                    Try Again
                                </button>
                            </div>
                        )}

                        {/* Empty State */}
                        {!isLoading && !error && gigs && gigs.length === 0 && (
                            <div className="rounded-2xl border border-slate-200 bg-white py-16 text-center dark:border-gray-800 dark:bg-gray-900">
                                <EyeSlashIcon className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                                <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-2">
                                    No gigs found
                                </h3>
                                <p className="text-gray-500 dark:text-gray-400 mb-6">
                                    {hasActiveFilters
                                        ? 'Try adjusting your filters to see more results.'
                                        : 'There are no gigs available at the moment.'
                                    }
                                </p>
                                {hasActiveFilters && (
                                    <button
                                        onClick={handleClearFilters}
                                        className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                                    >
                                        Clear Filters
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Gigs Grid */}
                        {!isLoading && !error && gigs && gigs.length > 0 && (
                            <>
                                {/* Regular Gigs Section Title */}
                                {!filters.featured && (
                                    <div className="mb-6">
                                        <h2 className="mb-2 flex items-center gap-2 text-xl font-semibold text-slate-900 dark:text-white">
                                            <span>All Available Gigs</span>
                                        </h2>
                                        <p className="text-sm text-slate-600 dark:text-gray-400">
                                            Discover exciting opportunities from trusted employers
                                        </p>
                                    </div>
                                )}

                                <div className={`grid grid-cols-1 gap-4 sm:gap-5 lg:gap-6 ${showFilters ? 'xl:grid-cols-2' : 'lg:grid-cols-2 xl:grid-cols-3'}`}>
                                    {gigs.map((gig) => {
                                        return (
                                            <GigCard
                                                key={gig._id}
                                                gig={gig}
                                                onFavorite={handleFavoriteToggle}
                                                isFavorited={favoriteGigs.has(gig._id)}
                                                currentUser={currentUser}
                                            />
                                        )
                                    })}
                                </div>

                                {/* Pagination */}
                                {totalPages > 1 && (
                                    <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900 sm:p-5">
                                        <div className="mb-3 text-sm text-slate-700 dark:text-gray-300 sm:mb-4">
                                            Showing {visibleFrom} to {visibleTo} of {totalCount} results
                                        </div>

                                        <div className="flex flex-wrap items-center gap-2">
                                            <button
                                                onClick={() => handlePageChange(currentPage - 1)}
                                                disabled={currentPage === 1}
                                                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                                            >
                                                Previous
                                            </button>

                                            {/* Page Numbers */}
                                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                                let pageNum: number
                                                if (totalPages <= 5) {
                                                    pageNum = i + 1
                                                } else if (currentPage <= 3) {
                                                    pageNum = i + 1
                                                } else if (currentPage > totalPages - 3) {
                                                    pageNum = totalPages - 4 + i
                                                } else {
                                                    pageNum = currentPage - 2 + i
                                                }

                                                return (
                                                    <button
                                                        key={pageNum}
                                                        onClick={() => handlePageChange(pageNum)}
                                                        className={`rounded-lg px-3 py-2 text-sm font-medium ${pageNum === currentPage
                                                            ? 'bg-primary-600 text-white shadow-sm'
                                                            : 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700'
                                                            }`}
                                                    >
                                                        {pageNum}
                                                    </button>
                                                )
                                            })}

                                            <button
                                                onClick={() => handlePageChange(currentPage + 1)}
                                                disabled={currentPage === totalPages}
                                                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                                            >
                                                Next
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Mobile Filters Modal */}
            <Modal
                isOpen={showMobileFiltersModal}
                onClose={() => setShowMobileFiltersModal(false)}
                title="Filter Gigs"
                size="full"
                className="md:hidden"
            >
                <div className="p-3 sm:p-4">
                    <GigFilters
                        filters={filters}
                        onFiltersChange={handleFiltersChange}
                        categories={categories}
                    />
                </div>
            </Modal>
        </>
    )
}

export default GigsPage